import { useInvoiceFormContext } from '@/components/Fiscal/Invoice/InvoiceFormProvider';
import {
  CnpjFormField,
  FloatingFormItem,
  InputDecimal,
  InputText,
} from '@/components/_common';
import { ipiCstOptions } from '@/core/domain/invoice/invoiceNoteItems/invoiceNoteItemsParts/InvoiceNoteItems.const';
import {
  InvoiceItemFormValues,
  IpiCst,
} from '@/core/domain/invoice/invoiceNoteItems/invoiceNoteItemsParts/InvoiceNoteItems.types';
import { InvoiceEmissionGoal } from '@/core/domain/invoice/invoices/invoices.enums';
import { useDefaultEmptyFieldError } from '@/hooks';
import { Card, Col, Form, FormInstance, Input, Row, Select } from 'antd';

interface NoteItemsIpiTabsFormProps {
  form: FormInstance<InvoiceItemFormValues>;
}

export default function NoteItemsIpiTabsForm({
  form,
}: NoteItemsIpiTabsFormProps) {
  const cstType = Form.useWatch('cstType', form);
  const cstIpi = Form.useWatch(['itemTaxes', 'cstIpi'], form);

  const returnedIpiValue = Form.useWatch(
    ['itemTaxes', 'returnedIpiValue'],
    form,
  );
  const returnedGoodsPercentage = Form.useWatch(
    ['itemTaxes', 'returnedGoodsPercentage'],
    form,
  );
  const defaultEmptyFieldError = useDefaultEmptyFieldError();

  const enableRuler = (rules: string[]) => {
    if (cstType === 1 && rules && cstIpi) {
      return !rules.includes(cstIpi);
    } else if (cstIpi && rules) {
      return !rules.includes(cstIpi);
    }
  };

  const {
    invoice: { data: invoice },
  } = useInvoiceFormContext();

  return (
    <Form form={form}>
      <Card title="IPI" type="inner" style={{ marginBottom: 24 }}>
        <Row gutter={[12, 12]}>
          <Col xs={24} sm={24} md={12} lg={5}>
            <CnpjFormField
              label="CNPJ do produtor da mercadoria"
              fieldName={['itemTaxes', 'ipiProducerCnpj']}
              required={false}
            />
          </Col>

          <Col xs={24} sm={24} md={12} lg={5}>
            <FloatingFormItem
              label="Cód. enquadramento"
              name={['itemTaxes', 'ipiLegalClassificationCode']}
            >
              <InputText onlyNumber />
            </FloatingFormItem>
          </Col>

          <Col xs={24} sm={24} md={12} lg={8}>
            <FloatingFormItem label="CST" name={['itemTaxes', 'ipiCst']}>
              <Select
                options={ipiCstOptions?.map((code) => ({
                  value: code.value,
                  label: code.label,
                }))}
                allowClear
              />
            </FloatingFormItem>
          </Col>

          <Col
            xs={24}
            sm={24}
            md={12}
            lg={6}
            hidden={enableRuler([
              IpiCst.OTHER_INPUTS,
              IpiCst.OUTPUT_TAXED,
              IpiCst.OTHER_OUTPUTS,
              IpiCst.INPUT_RECOVERY,
            ])}
          >
            <FloatingFormItem
              label="Alíquota"
              name={['itemTaxes', 'ipiAliquot']}
            >
              <InputDecimal fractions={4} max={100} />
            </FloatingFormItem>
          </Col>

          <Col
            xs={24}
            sm={24}
            md={12}
            lg={6}
            hidden={enableRuler([
              IpiCst.OTHER_INPUTS,
              IpiCst.OUTPUT_TAXED,
              IpiCst.OTHER_OUTPUTS,
              IpiCst.INPUT_RECOVERY,
            ])}
          >
            <FloatingFormItem
              label="Base de cálculo"
              name={['itemTaxes', 'ipiBaseCalculation']}
            >
              <InputDecimal />
            </FloatingFormItem>
          </Col>

          <Col
            xs={24}
            sm={24}
            md={12}
            lg={6}
            hidden={enableRuler([
              IpiCst.OTHER_INPUTS,
              IpiCst.OUTPUT_TAXED,
              IpiCst.OTHER_OUTPUTS,
              IpiCst.INPUT_RECOVERY,
            ])}
          >
            <FloatingFormItem label="Valor" name={['itemTaxes', 'ipiValue']}>
              <InputDecimal />
            </FloatingFormItem>
          </Col>
        </Row>
      </Card>

      <Card title="Selo controle" type="inner" style={{ marginBottom: 24 }}>
        <Row gutter={[12, 12]}>
          <Col
            xs={24}
            sm={24}
            md={12}
            lg={5}
            hidden={enableRuler([
              IpiCst.OTHER_INPUTS,
              IpiCst.OUTPUT_TAXED,
              IpiCst.OTHER_OUTPUTS,
              IpiCst.INPUT_RECOVERY,
            ])}
          >
            <FloatingFormItem
              label="Código"
              name={['itemTaxes', 'ipiControlSealCode']}
            >
              <Input maxLength={60} />
            </FloatingFormItem>
          </Col>

          <Col
            xs={24}
            sm={24}
            md={12}
            lg={5}
            hidden={enableRuler([
              IpiCst.OTHER_INPUTS,
              IpiCst.OUTPUT_TAXED,
              IpiCst.OTHER_OUTPUTS,
              IpiCst.INPUT_RECOVERY,
            ])}
          >
            <FloatingFormItem
              label="Quantidade"
              name={['itemTaxes', 'ipiControlSealQuantity']}
            >
              <InputText onlyNumber max={12} />
            </FloatingFormItem>
          </Col>
        </Row>
      </Card>

      <Card title="Unidade" type="inner" style={{ marginBottom: 24 }}>
        <Row gutter={[12, 12]}>
          <Col
            xs={24}
            sm={24}
            md={12}
            lg={5}
            hidden={enableRuler([
              IpiCst.OTHER_INPUTS,
              IpiCst.OUTPUT_TAXED,
              IpiCst.OTHER_OUTPUTS,
              IpiCst.INPUT_RECOVERY,
            ])}
          >
            <FloatingFormItem
              label="Valor"
              name={['itemTaxes', 'ipiUnitValue']}
            >
              <InputDecimal />
            </FloatingFormItem>
          </Col>

          <Col
            xs={24}
            sm={24}
            md={12}
            lg={5}
            hidden={enableRuler([
              IpiCst.OTHER_INPUTS,
              IpiCst.OUTPUT_TAXED,
              IpiCst.OTHER_OUTPUTS,
              IpiCst.INPUT_RECOVERY,
            ])}
          >
            <FloatingFormItem
              label="Quantidade"
              name={['itemTaxes', 'ipiUnitQuantity']}
            >
              <InputDecimal />
            </FloatingFormItem>
          </Col>
        </Row>
      </Card>

      {invoice?.emissionGoal === InvoiceEmissionGoal.NFE_MERCHANDISE_RETURN && (
        <Card title="Imposto devolvido">
          <Row gutter={[12, 12]}>
            <Col
              xs={24}
              sm={24}
              md={12}
              lg={5}
              hidden={enableRuler([
                IpiCst.OTHER_INPUTS,
                IpiCst.OUTPUT_TAXED,
                IpiCst.OTHER_OUTPUTS,
                IpiCst.INPUT_RECOVERY,
              ])}
            >
              <FloatingFormItem
                label="% Mercadoria devolvida"
                name={['itemTaxes', 'returnedGoodsPercentage']}
                rules={returnedIpiValue ? [defaultEmptyFieldError] : undefined}
              >
                <InputDecimal />
              </FloatingFormItem>
            </Col>

            <Col
              xs={24}
              sm={24}
              md={12}
              lg={5}
              hidden={enableRuler([
                IpiCst.OTHER_INPUTS,
                IpiCst.OUTPUT_TAXED,
                IpiCst.OTHER_OUTPUTS,
                IpiCst.INPUT_RECOVERY,
              ])}
            >
              <FloatingFormItem
                label="Valor ipi devolvido"
                name={['itemTaxes', 'returnedIpiValue']}
                rules={
                  returnedGoodsPercentage ? [defaultEmptyFieldError] : undefined
                }
              >
                <InputDecimal />
              </FloatingFormItem>
            </Col>
          </Row>
        </Card>
      )}
    </Form>
  );
}
